import React, { useState, useMemo, useEffect } from 'react';
import { View, Text, Pressable, TextInput, Modal, ScrollView, StyleSheet, Platform } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { DueDiligenceItem, ItemStatus, FlagType, STATUS_CONFIG } from '../../shared/types/due-diligence';
import { getItemTemplate } from '../../shared/due-diligence';
import { FlagBadge } from './FlagBadge';
import { FlagSelector } from './FlagSelector';
import { DataFieldsForm } from './DataFieldsForm';
import { FlagSuggestions } from './FlagSuggestions';
import { QuickContactsWidget } from './QuickContactsWidget';
import { Card } from '../ui';
import { Button } from '../ui/Button';
import { Ionicons } from '@expo/vector-icons';
import { AssigneeSelector } from './AssigneeSelector';

export interface ChecklistItemProps {
  item: DueDiligenceItem;
  portfolioId: number;
  propertyId: number;
  onUpdate: (updates: {
    status?: ItemStatus;
    flag?: FlagType | null;
    notes?: string;
    dataFields?: Record<string, any>;
    assignedTo?: number[];
  }) => Promise<void>;
  availablePartners: Array<{ id: number; email: string; display_name?: string }>;
  loading?: boolean;
}

export function ChecklistItem({ 
  item,
  portfolioId,
  propertyId,
  onUpdate,
  availablePartners,
  loading = false
}: ChecklistItemProps) {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const { isMobile, isSmallMobile } = useResponsive();
  const [notesModalVisible, setNotesModalVisible] = useState(false);
  const [notesText, setNotesText] = useState(item.notes || '');
  const [updating, setUpdating] = useState(false);
  const [localDataFields, setLocalDataFields] = useState<Record<string, any>>(item.dataFields || {});
  const [dataFieldsDirty, setDataFieldsDirty] = useState(false);
  const itemStyles = makeItemStyles(tokens, utils, isMobile, isSmallMobile);

  // Get the item template with dataFields and suggestedFlags definitions
  const itemTemplate = useMemo(() => {
    if (!item?.module || !item?.code) return null;
    return getItemTemplate(item.module, item.code);
  }, [item?.module, item?.code]);

  // Sync local data fields with item when item changes
  useEffect(() => {
    setLocalDataFields(item.dataFields || {});
    setDataFieldsDirty(false);
  }, [item.dataFields]);

  // Safe access with fallbacks
  const safeStatus = item?.status || 'pending';
  const statusConfig = STATUS_CONFIG[safeStatus] || STATUS_CONFIG.pending;
  const hasNotes = !!item?.notes && item.notes.trim().length > 0;
  const hasAssignments = item?.assignedTo && item.assignedTo.length > 0;
  const hasDataFields = itemTemplate?.dataFields && itemTemplate.dataFields.length > 0;
  const hasFilledDataFields = Object.values(localDataFields).some(v => v !== null && v !== undefined && v !== '');

  const handleStatusChange = async (newStatus: ItemStatus) => {
    if (newStatus === safeStatus) return;
    setUpdating(true);
    try {
      await onUpdate({ status: newStatus });
    } finally {
      setUpdating(false);
    }
  };

  const handleFlagChange = async (flag: FlagType | null) => {
    setUpdating(true);
    try {
      await onUpdate({ flag: flag || undefined });
    } finally {
      setUpdating(false);
    }
  };

  const handleNotesSave = async () => {
    setUpdating(true);
    try {
      await onUpdate({ notes: notesText });
      setNotesModalVisible(false);
    } finally {
      setUpdating(false);
    }
  };

  const handleAssigneeChange = async (userIds: number[]) => {
    setUpdating(true);
    try {
      await onUpdate({ assignedTo: userIds });
    } finally {
      setUpdating(false);
    }
  };

  const handleDataFieldChange = (key: string, value: any) => {
    setLocalDataFields(prev => ({ ...prev, [key]: value }));
    setDataFieldsDirty(true);
  };

  const handleSaveDataFields = async () => {
    setUpdating(true);
    try {
      await onUpdate({ dataFields: localDataFields });
      setDataFieldsDirty(false);
    } finally {
      setUpdating(false);
    }
  };

  const handleAcceptFlagSuggestion = async (flag: FlagType) => {
    setUpdating(true);
    try {
      await onUpdate({ flag });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <Card
        style={[
          itemStyles.card,
          item.flag === 'big_red' && itemStyles.cardBigRed,
          item.flag === 'red' && itemStyles.cardRed,
          item.flag === 'green' && itemStyles.cardGreen,
          item.flag === 'big_green' && itemStyles.cardBigGreen,
        ]}
        padding="md"
        elevated
      >
        {/* Header */}
        <View style={itemStyles.header}>
          <View style={itemStyles.headerLeft}>
            <View style={itemStyles.titleRow}>
              <Text style={itemStyles.title} numberOfLines={2}>
                {item.title}
              </Text>
              {item.isGroupTask && (
                <View style={itemStyles.groupTaskBadge}>
                  <Ionicons name="people" size={12} color={tokens.color.primary} />
                  <Text style={itemStyles.groupTaskText}>Group</Text>
                </View>
              )}
            </View>
            {item.description && !isSmallMobile && (
              <Text style={itemStyles.description} numberOfLines={2}>
                {item.description}
              </Text>
            )}
            <View style={itemStyles.metaRow}>
              <Text style={itemStyles.category}>
                {item.module} • {item.category}
              </Text>
            </View>
          </View>
        </View>

        {/* Status and Flag */}
        <View style={itemStyles.controlsRow}>
          {/* Status Selector */}
          <View style={itemStyles.statusContainer}>
            <Text style={itemStyles.controlLabel}>Status</Text>
            <View style={itemStyles.statusButtons}>
              {(['pending', 'in_progress', 'complete', 'not_applicable'] as ItemStatus[]).map((status) => {
                const config = STATUS_CONFIG[status];
                if (!config) return null;
                const isSelected = safeStatus === status;
                return (
                  <Pressable
                    key={status}
                    onPress={() => handleStatusChange(status)}
                    disabled={updating || loading}
                    style={({ pressed }) => [
                      itemStyles.statusButton,
                      isSelected && itemStyles.statusButtonSelected,
                      (pressed || updating || loading) && itemStyles.statusButtonPressed,
                    ]}
                  >
                    <Text style={[
                      itemStyles.statusButtonText,
                      isSelected && itemStyles.statusButtonTextSelected,
                    ]}>
                      {config.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          {/* Flag Selector */}
          <View style={itemStyles.flagContainer}>
            <Text style={itemStyles.controlLabel}>Flag</Text>
            <FlagSelector
              value={item.flag || null}
              onSelect={handleFlagChange}
              size="sm"
              variant="badge"
            />
          </View>
        </View>

        {/* Assignees */}
        {item.isGroupTask && (
          <View style={itemStyles.assigneesContainer}>
            <Text style={itemStyles.controlLabel}>Assigned To</Text>
            <AssigneeSelector
              value={item.assignedTo || []}
              onSelect={handleAssigneeChange}
              availablePartners={availablePartners}
              size="sm"
            />
          </View>
        )}

        {/* Notes Button */}
        <Pressable
          onPress={() => setNotesModalVisible(true)}
          style={({ pressed }) => [
            itemStyles.notesButton,
            hasNotes && itemStyles.notesButtonHasNotes,
            pressed && itemStyles.notesButtonPressed,
          ]}
        >
          <Ionicons
            name={hasNotes ? "document-text" : "document-text-outline"}
            size={16}
            color={hasNotes ? tokens.color.primary : tokens.color.onSurface}
          />
          <Text style={[
            itemStyles.notesButtonText,
            hasNotes && itemStyles.notesButtonTextHasNotes,
          ]}>
            {hasNotes ? 'View Notes' : 'Add Notes'}
          </Text>
          {hasNotes && (
            <View style={itemStyles.notesIndicator} />
          )}
        </Pressable>

        {/* Data Fields Form */}
        {hasDataFields && (
          <>
            <DataFieldsForm
              fields={itemTemplate.dataFields!}
              values={localDataFields}
              onChange={handleDataFieldChange}
              disabled={updating || loading}
            />
            {dataFieldsDirty && (
              <View style={itemStyles.dataFieldsActions}>
                <Button
                  title="Save Data"
                  onPress={handleSaveDataFields}
                  variant="primary"
                  size="sm"
                  loading={updating}
                />
                <Button
                  title="Reset"
                  onPress={() => {
                    setLocalDataFields(item.dataFields || {});
                    setDataFieldsDirty(false);
                  }}
                  variant="outline"
                  size="sm"
                />
              </View>
            )}
          </>
        )}

        {/* Flag Suggestions */}
        {itemTemplate?.suggestedFlags && itemTemplate.suggestedFlags.length > 0 && hasFilledDataFields && (
          <FlagSuggestions
            suggestedFlags={itemTemplate.suggestedFlags}
            dataFields={itemTemplate.dataFields}
            dataValues={localDataFields}
            currentFlag={item.flag}
            onAcceptSuggestion={handleAcceptFlagSuggestion}
            itemCode={item.code}
          />
        )}

        {/* Quick Contacts - Show relevant contacts from you and partners */}
        <View style={itemStyles.quickContactsContainer}>
          <QuickContactsWidget
            portfolioId={portfolioId}
            propertyId={propertyId}
            category={item.category}
          />
        </View>
      </Card>

      {/* Notes Modal */}
      <Modal
        visible={notesModalVisible}
        transparent
        animationType="slide"
        onRequestClose={() => setNotesModalVisible(false)}
      >
        <View style={itemStyles.modalOverlay}>
          <View style={itemStyles.modalContent}>
            <View style={itemStyles.modalHeader}>
              <Text style={itemStyles.modalTitle}>Notes: {item.title}</Text>
              <Pressable
                onPress={() => setNotesModalVisible(false)}
                style={itemStyles.modalCloseButton}
              >
                <Ionicons name="close" size={24} color={tokens.color.onSurface} />
              </Pressable>
            </View>
            <ScrollView style={itemStyles.modalBody}>
              <TextInput
                style={itemStyles.notesInput}
                value={notesText}
                onChangeText={setNotesText}
                placeholder="Add notes about this checklist item..."
                placeholderTextColor={addColorOpacity(tokens.color.onSurface, 0.5)}
                multiline
                textAlignVertical="top"
              />
            </ScrollView>
            <View style={itemStyles.modalFooter}>
              <Button
                title="Cancel"
                onPress={() => {
                  setNotesText(item.notes || '');
                  setNotesModalVisible(false);
                }}
                variant="outline"
                style={{ flex: 1 }}
              />
              <Button
                title="Save"
                onPress={handleNotesSave}
                variant="primary"
                loading={updating}
                style={{ flex: 1 }}
              />
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
}

function makeItemStyles(
  tokens: ReturnType<typeof useStyles>['tokens'],
  utils: ReturnType<typeof useStyles>['utils'],
  isMobile: boolean,
  isSmallMobile: boolean
) {
  return StyleSheet.create({
    card: {
      marginBottom: utils.spacing.md,
      borderLeftWidth: 4,
      borderLeftColor: 'transparent',
    },
    cardBigRed: {
      borderLeftColor: '#DC2626',
      backgroundColor: addColorOpacity('#DC2626', 0.05),
    },
    cardRed: {
      borderLeftColor: '#EF4444',
      backgroundColor: addColorOpacity('#EF4444', 0.03),
    },
    cardGreen: {
      borderLeftColor: '#22C55E',
    },
    cardBigGreen: {
      borderLeftColor: '#16A34A',
      backgroundColor: addColorOpacity('#16A34A', 0.05),
    },
    header: {
      marginBottom: utils.spacing.md,
    },
    headerLeft: {
      flex: 1,
    },
    titleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.xs,
      marginBottom: utils.spacing.xs,
      flexWrap: 'wrap',
    },
    title: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onBackground,
      fontFamily: utils.typography.heading.fontFamily,
      flex: 1,
    },
    groupTaskBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 2,
      paddingHorizontal: utils.spacing.xs,
      paddingVertical: 2,
      borderRadius: utils.radius.sm,
      backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
    },
    groupTaskText: {
      fontSize: utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.medium,
      color: tokens.color.primary,
      fontFamily: utils.typography.body.fontFamily,
    },
    description: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.7),
      marginBottom: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
    },
    metaRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.sm,
      marginTop: utils.spacing.xs,
    },
    category: {
      fontSize: utils.typography.sizes.xs,
      color: addColorOpacity(tokens.color.onSurface, 0.5),
      fontFamily: utils.typography.body.fontFamily,
      textTransform: 'capitalize',
    },
    controlsRow: {
      flexDirection: isMobile ? 'column' : 'row',
      gap: utils.spacing.md,
      marginBottom: utils.spacing.md,
      ...(isMobile && {
        gap: utils.spacing.sm,
      }),
    },
    statusContainer: {
      flex: isMobile ? 1 : 0,
      minWidth: isMobile ? '100%' : 200,
    },
    flagContainer: {
      flex: isMobile ? 1 : 0,
      minWidth: isMobile ? '100%' : 150,
    },
    assigneesContainer: {
      marginBottom: utils.spacing.md,
    },
    controlLabel: {
      fontSize: utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.medium,
      color: addColorOpacity(tokens.color.onSurface, 0.7),
      marginBottom: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    statusButtons: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: utils.spacing.xs,
    },
    statusButton: {
      paddingHorizontal: utils.spacing.sm,
      paddingVertical: utils.spacing.xs,
      borderRadius: utils.radius.sm,
      backgroundColor: tokens.color.surface,
      borderWidth: 1,
      borderColor: tokens.color.border,
      ...Platform.select({
        web: {
          cursor: 'pointer',
        } as any,
      }),
    },
    statusButtonSelected: {
      backgroundColor: tokens.color.primary,
      borderColor: tokens.color.primary,
    },
    statusButtonPressed: {
      opacity: 0.7,
    },
    statusButtonText: {
      fontSize: utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.medium,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    statusButtonTextSelected: {
      color: tokens.color.onPrimary,
    },
    notesButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.xs,
      paddingVertical: utils.spacing.sm,
      paddingHorizontal: utils.spacing.md,
      borderRadius: utils.radius.md,
      backgroundColor: addColorOpacity(tokens.color.onSurface, 0.05),
      borderWidth: 1,
      borderColor: tokens.color.border,
      ...Platform.select({
        web: {
          cursor: 'pointer',
        } as any,
      }),
    },
    notesButtonHasNotes: {
      backgroundColor: addColorOpacity(tokens.color.primary, 0.05),
      borderColor: tokens.color.primary,
    },
    notesButtonPressed: {
      opacity: 0.7,
    },
    notesButtonText: {
      fontSize: utils.typography.sizes.sm,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      flex: 1,
    },
    notesButtonTextHasNotes: {
      color: tokens.color.primary,
      fontWeight: utils.typography.weights.medium,
    },
    notesIndicator: {
      width: 6,
      height: 6,
      borderRadius: 3,
      backgroundColor: tokens.color.primary,
    },
    dataFieldsActions: {
      flexDirection: 'row',
      gap: utils.spacing.sm,
      marginTop: utils.spacing.md,
      justifyContent: 'flex-end',
    },
    quickContactsContainer: {
      marginTop: utils.spacing.md,
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'flex-end',
    },
    modalContent: {
      backgroundColor: tokens.color.surface,
      borderTopLeftRadius: utils.radius.xl,
      borderTopRightRadius: utils.radius.xl,
      maxHeight: '90%',
      ...Platform.select({
        web: {
          borderTopLeftRadius: utils.radius.lg,
          borderTopRightRadius: utils.radius.lg,
        } as any,
      }),
    },
    modalHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: utils.spacing.lg,
      borderBottomWidth: 1,
      borderBottomColor: tokens.color.border,
    },
    modalTitle: {
      fontSize: utils.typography.sizes.lg,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onBackground,
      fontFamily: utils.typography.heading.fontFamily,
      flex: 1,
    },
    modalCloseButton: {
      padding: utils.spacing.xs,
      ...Platform.select({
        web: {
          cursor: 'pointer',
        } as any,
      }),
    },
    modalBody: {
      padding: utils.spacing.lg,
      maxHeight: 400,
    },
    notesInput: {
      minHeight: 200,
      borderWidth: 1,
      borderColor: tokens.color.border,
      borderRadius: utils.radius.md,
      padding: utils.spacing.md,
      backgroundColor: tokens.color.surface,
      color: tokens.color.onSurface,
      fontSize: utils.typography.sizes.base,
      fontFamily: utils.typography.body.fontFamily,
      ...Platform.select({
        web: {
          outline: 'none',
        } as any,
      }),
    },
    modalFooter: {
      flexDirection: 'row',
      gap: utils.spacing.md,
      padding: utils.spacing.lg,
      borderTopWidth: 1,
      borderTopColor: tokens.color.border,
    },
  });
}

